import { createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import {
  isCustomerSecuritiesRequestParams,
  streamingDataSlice,
} from '@src/providers/AppStateProvider/streamingDataSlice';
import type { AppState, AppStateListenerStart } from '@src/providers/AppStateProvider/types';
import {
  BaseField,
  calculatePrecision,
  Field,
  getTypedKeys,
  MultiSelectorField,
  NumericField,
  OrderFormSides,
  percentToBps,
  QuoteStatusEnum,
  SelectorField,
  Unit,
  type Customer,
  type CustomerBalance,
  type CustomerQuote,
  type CustomerSpecificSecurity,
  type MarketAccount,
  type Security,
} from '@talos/kyoko';
import Big from 'big.js';
import type { WritableDraft } from 'immer';
import { isEqual, omit } from 'lodash-es';
import { getDefaultMarketAccountForCustomer, getQuantityIncrement } from '../Common';
import { initialRefDataState } from '../OMSReferenceDataSlice';
import { numberIsPositive } from '../commonFieldRules';
import type { OMSReferenceDataState } from '../types';
import { getSelectableCustomers } from '../utils';
import { saleQuantityIsAccountedFor } from './FieldRules';
import type { SalesRFQDependencies, SalesRFQInitPayload, SalesRFQState } from './types';
import { selectSalesRFQCustomerSecuritiesRequestParams } from './utils/customerSecuritySelector';
import {
  refreshEffectivePricingRule,
  selectBidSpreadFieldDisplayValue,
  selectEffectivePricingRule,
  selectEffectivePricingRuleRequestParams,
  selectEffectivePricingRuleResponse,
  selectOfferSpreadFieldDisplayValue,
} from './utils/effectivePricingRuleSelector';

export const getInitialState = (): SalesRFQState => ({
  referenceData: initialRefDataState,
  dependencies: {
    availableRFQMarketAccounts: [],
  },
  form: {
    customerField: new SelectorField({ idProperty: 'Name' }),
    customerAccountField: new SelectorField({ idProperty: 'Name' }),
    symbolField: new SelectorField({ idProperty: 'Symbol' }),
    sideField: new Field({ value: OrderFormSides.Twoway }),
    customerQuantityField: new NumericField({ name: 'Quantity' }),
    rfqCurrencyField: new Field(),
    bidSpreadField: new NumericField({ unit: Unit.Bps, name: 'Spread' }),
    offerSpreadField: new NumericField({ unit: Unit.Bps, name: 'Spread' }),
    bidPriceField: new NumericField({ displayTrailingZeroes: true }),
    offerPriceField: new NumericField({ displayTrailingZeroes: true }),
    quoteTtlField: new Field({ name: 'Quote TTL', isRequired: false }),
    rfqTtlField: new Field({ name: 'RFQ TTL', isRequired: false }),
    useDefaultAggregationField: new Field({ value: 'true' }),
    rfqMarketsField: new MultiSelectorField(),
  },
  isOpen: false,
  isLoading: false,
  isCustomerPriceEditable: false,
  currentQuote: undefined,
});

const resetEconomics = (state: WritableDraft<SalesRFQState>) => {
  state.isCustomerPriceEditable = false;
  state.isLoading = false;
  state.form.bidPriceField = new NumericField({ displayTrailingZeroes: true });
  state.form.offerPriceField = new NumericField({ displayTrailingZeroes: true });
  // remove the effective spreads from the current quote. This is to ensure we do not display any old quote values when we clear the quote.
  state.currentQuote = omit(state.currentQuote, 'EffectiveBidSpread', 'EffectiveOfferSpread');
};

const disableInputFields = (state: WritableDraft<SalesRFQState>, isDisabled: boolean) => {
  state.form.sideField = state.form.sideField.setDisabled(isDisabled);
  state.form.customerField = state.form.customerField.setDisabled(isDisabled);
  state.form.customerAccountField = state.form.customerAccountField.setDisabled(isDisabled);
  state.form.symbolField = state.form.symbolField.setDisabled(isDisabled);
  state.form.customerQuantityField = state.form.customerQuantityField.setDisabled(isDisabled);
  state.form.rfqMarketsField = state.form.rfqMarketsField.setDisabled(isDisabled);
  state.form.quoteTtlField = state.form.quoteTtlField.setDisabled(isDisabled);
  state.form.rfqTtlField = state.form.rfqTtlField.setDisabled(isDisabled);
};

const validate = (state: WritableDraft<SalesRFQState>) => {
  state.form.customerQuantityField = state.form.customerQuantityField.validate(
    [numberIsPositive, saleQuantityIsAccountedFor],
    state
  );
};

const populateDropdownsFromRefData = (state: WritableDraft<SalesRFQState>) => {
  if (state.form.customerField.isDisabled || state.isLoading) {
    return;
  }

  const { customers, customerMarketAccounts, customerConfigurations } = state.referenceData;
  const previousCustomerName = state.form.customerField.value?.Name;
  const hadCustomer = previousCustomerName != null;
  state.form.customerField = state.form.customerField.updateAvailableItems(
    getSelectableCustomers(customers, customerConfigurations),
    { preventDefaultToValue: hadCustomer }
  );

  const initialCustomer = state.form.customerField.availableItems.find(
    c => c.CounterpartyID === state.initialPayload?.initialCustomerID
  );
  if (!state.form.customerField.value && initialCustomer && !hadCustomer) {
    state.form.customerField = state.form.customerField.updateValue(initialCustomer, true);
  }

  const customerForAccount = state.form.customerField.value;
  if (!customerForAccount) {
    state.form.customerAccountField = state.form.customerAccountField
      .updateAvailableItems([], { preventDefaultToValue: true })
      .updateValue(undefined, true)
      .setDisabled(false);
    return;
  }

  const selectedCustomerChanged = customerForAccount.Name !== previousCustomerName;
  if (!state.form.customerAccountField.value || selectedCustomerChanged) {
    state.form.customerAccountField = getDefaultMarketAccountForCustomer(
      state.form.customerAccountField,
      customerMarketAccounts,
      customerForAccount
    );
  }
};

const handleSymbolChange = (state: WritableDraft<SalesRFQState>, symbol?: string) => {
  const security = state.form.symbolField.availableItems.find(s => s.Symbol === symbol);
  if (!security) {
    return;
  }

  state.form.symbolField = state.form.symbolField.updateValue(security);

  const { BaseCurrency, QuoteCurrency } = security || {};
  if (state.form.rfqCurrencyField.value !== BaseCurrency && state.form.rfqCurrencyField.value !== QuoteCurrency) {
    state.form.rfqCurrencyField = state.form.rfqCurrencyField.updateValue(BaseCurrency, true);
  }
  state.form.customerQuantityField = state.form.customerQuantityField
    .updateValue('', true)
    .updateScale(getQuantityIncrement(security, state.form.rfqCurrencyField.value));
};

export function setupSalesRFQListeners(startListening: AppStateListenerStart) {
  // Whenever the effective pricing rule request to be made changes, invalidate and re-fetch.
  const unsubscribeRequestEffectivePricingRuleListener = startListening({
    predicate: (_, next, prev) => {
      return !isEqual(selectEffectivePricingRuleRequestParams(prev), selectEffectivePricingRuleRequestParams(next));
    },
    effect: (_, listener) => {
      const requestParams = selectEffectivePricingRuleRequestParams(listener.getState());
      if (requestParams == null) {
        return;
      }

      listener.dispatch(refreshEffectivePricingRule());
    },
  });

  const unsubscribeCustomerSecuritiesListener = startListening({
    predicate: (_, next, prev) => {
      return !isEqual(
        selectSalesRFQCustomerSecuritiesRequestParams(prev),
        selectSalesRFQCustomerSecuritiesRequestParams(next)
      );
    },
    effect: async (_, listener) => {
      const requestParams = selectSalesRFQCustomerSecuritiesRequestParams(listener.getState());
      if (!isCustomerSecuritiesRequestParams(requestParams)) {
        return;
      }
      const request = listener.dispatch(streamingDataSlice.endpoints.getCustomerSecurities.initiate(requestParams));
      const res = await request.unwrap();
      request.unsubscribe();
      const principalSecurityMap = listener.getState().salesRFQ.referenceData.securities.securitiesBySymbol;

      const availableCustomerSecurities = Array.from(res.values())
        .filter(security => security.Symbol)
        .map(security => {
          return {
            ...principalSecurityMap.get(security.Symbol),
            ...security,
          } as CustomerSpecificSecurity;
        });
      if (availableCustomerSecurities.length) {
        listener.dispatch(setAvailableCustomerSecurities(availableCustomerSecurities));
      }
    },
  });

  // Auto-populate spread fields when effective pricing rule loads
  const unsubscribeEffectivePricingRuleAutoFillListener = startListening({
    predicate: (_, next, prev) => {
      const nextResponse = selectEffectivePricingRuleResponse(next);
      const prevRule = selectEffectivePricingRule(prev);
      const nextRule = selectEffectivePricingRule(next);
      const nextQuote = next.salesRFQ.currentQuote;

      // Only auto-fill if:
      // 1. Effective pricing rule changed and we have data (not loading)
      // 2. We don't have a current quote (or quote doesn't have effective spreads)
      return !!(
        nextRule !== prevRule &&
        nextResponse?.data?.[0] &&
        !nextResponse.isLoading &&
        (!nextQuote || (!nextQuote.EffectiveBidSpread && !nextQuote.EffectiveOfferSpread))
      );
    },
    effect: (_, listener) => {
      const state = listener.getState();
      const effectivePricingRule = selectEffectivePricingRule(state);
      const bidSpreadField = state.salesRFQ.form.bidSpreadField;
      const offerSpreadField = state.salesRFQ.form.offerSpreadField;

      // Set spread values from the new pricing rule unless the field is currently focused (user is editing)
      if (effectivePricingRule?.BidSpread && !bidSpreadField.isFocused) {
        const bidSpreadValue = percentToBps(effectivePricingRule.BidSpread);
        if (bidSpreadValue) {
          listener.dispatch(setSpreadBid(bidSpreadValue));
        }
      }

      if (effectivePricingRule?.OfferSpread && !offerSpreadField.isFocused) {
        const offerSpreadValue = percentToBps(effectivePricingRule.OfferSpread);
        if (offerSpreadValue) {
          listener.dispatch(setSpreadOffer(offerSpreadValue));
        }
      }

      const { quoteTtlField, rfqTtlField } = state.salesRFQ.form;
      if (!quoteTtlField.isFocused) {
        listener.dispatch(setQuoteTtl(effectivePricingRule?.QuoteTTL ?? ''));
      }
      if (!rfqTtlField.isFocused) {
        listener.dispatch(setRfqTtl(effectivePricingRule?.RFQTTL ?? ''));
      }
    },
  });

  // Clear spread/TTL fields when a pricing rule request completes with no matching rule,
  // or when the request fails. The auto-fill listener only fires when data IS present;
  // this covers the opposite cases so stale values from a previous rule don't linger.
  const unsubscribeEffectivePricingRuleClearListener = startListening({
    predicate: (_, next, prev) => {
      const prevResponse = selectEffectivePricingRuleResponse(prev);
      const nextResponse = selectEffectivePricingRuleResponse(next);
      const nextQuote = next.salesRFQ.currentQuote;
      const nextResponseHasNoRule = (nextResponse?.isSuccess && !nextResponse?.data?.[0]) || !!nextResponse?.isError;

      return !!(
        prevResponse?.isLoading &&
        nextResponseHasNoRule &&
        (!nextQuote || (!nextQuote.EffectiveBidSpread && !nextQuote.EffectiveOfferSpread))
      );
    },
    effect: (_, listener) => {
      const { form } = listener.getState().salesRFQ;
      if (!form.bidSpreadField.isFocused) {
        listener.dispatch(setSpreadBid(''));
      }
      if (!form.offerSpreadField.isFocused) {
        listener.dispatch(setSpreadOffer(''));
      }
      if (!form.quoteTtlField.isFocused) {
        listener.dispatch(setQuoteTtl(''));
      }
      if (!form.rfqTtlField.isFocused) {
        listener.dispatch(setRfqTtl(''));
      }
    },
  });

  // Re-populate spread fields from the effective pricing rule when a quote is canceled or rejected
  const unsubscribeCanceledRejectedQuoteResetListener = startListening({
    predicate: (_, next, prev) => {
      const nextQuoteStatus = next.salesRFQ.currentQuote?.QuoteStatus;
      const prevQuoteStatus = prev.salesRFQ.currentQuote?.QuoteStatus;
      return (
        nextQuoteStatus !== prevQuoteStatus &&
        (nextQuoteStatus === QuoteStatusEnum.Rejected || nextQuoteStatus === QuoteStatusEnum.Canceled)
      );
    },
    effect: (_, listener) => {
      const state = listener.getState();
      const effectivePricingRule = selectEffectivePricingRule(state);

      if (effectivePricingRule?.BidSpread) {
        const bidSpreadValue = percentToBps(effectivePricingRule.BidSpread);
        if (bidSpreadValue) {
          listener.dispatch(setSpreadBid(bidSpreadValue));
        }
      }

      if (effectivePricingRule?.OfferSpread) {
        const offerSpreadValue = percentToBps(effectivePricingRule.OfferSpread);
        if (offerSpreadValue) {
          listener.dispatch(setSpreadOffer(offerSpreadValue));
        }
      }

      listener.dispatch(setQuoteTtl(effectivePricingRule?.QuoteTTL ?? ''));
      listener.dispatch(setRfqTtl(effectivePricingRule?.RFQTTL ?? ''));
    },
  });

  return () => {
    unsubscribeRequestEffectivePricingRuleListener();
    unsubscribeCustomerSecuritiesListener();
    unsubscribeEffectivePricingRuleAutoFillListener();
    unsubscribeEffectivePricingRuleClearListener();
    unsubscribeCanceledRejectedQuoteResetListener();
  };
}

const salesRFQSlice = createSlice({
  name: 'salesRFQ',
  initialState: getInitialState(),
  reducers: {
    setReferenceData: (state, action: PayloadAction<OMSReferenceDataState>) => {
      const customersOrConfigsChanged =
        state.referenceData.customers !== action.payload.customers ||
        state.referenceData.customerConfigurations !== action.payload.customerConfigurations;

      state.referenceData = action.payload;

      if (state.initialPayload == null) {
        return;
      }

      if (customersOrConfigsChanged) {
        populateDropdownsFromRefData(state);
      } else if (!state.form.customerField.value) {
        state.form.customerAccountField = getDefaultMarketAccountForCustomer(
          state.form.customerAccountField,
          action.payload.customerMarketAccounts,
          undefined
        );
      }
    },
    onMount: (state, action: PayloadAction<SalesRFQInitPayload>) => {
      state.initialPayload = action.payload;
      populateDropdownsFromRefData(state);
    },
    setDependencies: (state, action: PayloadAction<SalesRFQDependencies>) => {
      state.dependencies.availableRFQMarketAccounts = action.payload.availableRFQMarketAccounts;
      state.dependencies.customerBalances = action.payload.customerBalances;

      state.form.rfqMarketsField = state.form.rfqMarketsField.updateAvailableItems(
        action.payload.availableRFQMarketAccounts
      );
    },
    setAvailableCustomerSecurities: (state, action: PayloadAction<Security[]>) => {
      state.form.symbolField = state.form.symbolField.updateAvailableItems(action.payload);
      if (action.payload?.length === 1) {
        handleSymbolChange(state, action.payload[0].Symbol);
      }

      handleSymbolChange(state, state.form.symbolField.value?.Symbol);
    },
    setSide: (state, action: PayloadAction<OrderFormSides>) => {
      state.form.sideField = state.form.sideField.updateValue(action.payload);
      validate(state);
    },
    setCustomer: (state, { payload: customer }: PayloadAction<Customer>) => {
      state.form.customerField = state.form.customerField.updateValue(customer);
      state.form.customerAccountField = getDefaultMarketAccountForCustomer(
        state.form.customerAccountField,
        state.referenceData.customerMarketAccounts,
        customer
      );
      validate(state);
    },
    setCustomerAccount: (state, action: PayloadAction<MarketAccount>) => {
      state.form.customerAccountField = state.form.customerAccountField.updateValue(action.payload);
      validate(state);
    },
    setSymbol: (state, action: PayloadAction<string | undefined>) => {
      handleSymbolChange(state, action.payload);
    },
    setCustomerQuantity: (state, action: PayloadAction<string>) => {
      state.form.customerQuantityField = state.form.customerQuantityField.updateValue(action.payload);
      validate(state);
    },
    setRFQCurrency: (state, action: PayloadAction<string>) => {
      state.form.rfqCurrencyField = state.form.rfqCurrencyField.updateValue(action.payload);
      state.form.customerQuantityField = state.form.customerQuantityField
        .updateValue('', true)
        .updateScale(getQuantityIncrement(state.form.symbolField.value, action.payload));
    },
    setSpreadBid: (state, action: PayloadAction<string>) => {
      state.form.bidSpreadField = state.form.bidSpreadField.updateValue(action.payload);
    },
    setSpreadOffer: (state, action: PayloadAction<string>) => {
      state.form.offerSpreadField = state.form.offerSpreadField.updateValue(action.payload);
    },
    setBidSpreadFocused: (state, action: PayloadAction<boolean>) => {
      state.form.bidSpreadField = state.form.bidSpreadField.setFocused(action.payload);
    },
    setOfferSpreadFocused: (state, action: PayloadAction<boolean>) => {
      state.form.offerSpreadField = state.form.offerSpreadField.setFocused(action.payload);
    },
    setQuoteTtl: (state, action: PayloadAction<string>) => {
      state.form.quoteTtlField = state.form.quoteTtlField.updateValue(action.payload);
    },
    setRfqTtl: (state, action: PayloadAction<string>) => {
      state.form.rfqTtlField = state.form.rfqTtlField.updateValue(action.payload);
    },
    setQuoteTtlFocused: (state, action: PayloadAction<boolean>) => {
      state.form.quoteTtlField = state.form.quoteTtlField.setFocused(action.payload);
    },
    setRfqTtlFocused: (state, action: PayloadAction<boolean>) => {
      state.form.rfqTtlField = state.form.rfqTtlField.setFocused(action.payload);
    },
    setBidPriceFocused: (state, action: PayloadAction<boolean>) => {
      state.form.bidPriceField = state.form.bidPriceField.setFocused(action.payload);
    },
    setOfferPriceFocused: (state, action: PayloadAction<boolean>) => {
      state.form.offerPriceField = state.form.offerPriceField.setFocused(action.payload);
    },
    setCustomerBidPrice: (state, action: PayloadAction<string>) => {
      // Reject invalid input by reverting to the previous value. However, if the user is typing more decimal
      // places than the quote's precision (scale), expand the scale first so the input is not silently rejected.
      const previousValue = state.form.bidPriceField.value;
      const userPrecision = calculatePrecision(action.payload);
      const currentScale = state.form.bidPriceField.scale ?? 0;
      const field =
        userPrecision > currentScale ? state.form.bidPriceField.updateScale(userPrecision) : state.form.bidPriceField;
      state.form.bidPriceField = field.updateValue(action.payload);
      if (state.form.bidPriceField.hasError) {
        state.form.bidPriceField = state.form.bidPriceField.updateValue(previousValue);
      }
    },
    setCustomerOfferPrice: (state, action: PayloadAction<string>) => {
      const previousValue = state.form.offerPriceField.value;
      const userPrecision = calculatePrecision(action.payload);
      const currentScale = state.form.offerPriceField.scale ?? 0;
      const field =
        userPrecision > currentScale
          ? state.form.offerPriceField.updateScale(userPrecision)
          : state.form.offerPriceField;
      state.form.offerPriceField = field.updateValue(action.payload);
      if (state.form.offerPriceField.hasError) {
        state.form.offerPriceField = state.form.offerPriceField.updateValue(previousValue);
      }
    },
    setCurrentQuote: (state, { payload: quote }: PayloadAction<CustomerQuote>) => {
      state.currentQuote = quote;
      state.isLoading = false;

      // reset input fields & economics if rejected or cancelled
      if (quote.QuoteStatus === QuoteStatusEnum.Rejected || quote.QuoteStatus === QuoteStatusEnum.Canceled) {
        resetEconomics(state);
        disableInputFields(state, false);
      } else {
        disableInputFields(state, true);
        if (quote.EffectiveBidSpread) {
          // if this field is currently focused, do not update it.
          if (!state.form.bidSpreadField.isFocused) {
            state.form.bidSpreadField = state.form.bidSpreadField.updateValue(
              (quote.EffectiveBidSpread as string) || '',
              true
            );
          }
        }
        if (quote.EffectiveOfferSpread) {
          // if this field is currently focused, do not update it.
          if (!state.form.offerSpreadField.isFocused) {
            state.form.offerSpreadField = state.form.offerSpreadField.updateValue(
              (quote.EffectiveOfferSpread as string) || '',
              true
            );
          }
        }
        // consider not updating the price fields, if the user is currently editing the price fields.
        if (quote.BidPx) {
          state.form.bidPriceField = state.form.bidPriceField
            .updateValue(quote.BidPx, true)
            .updateScale(calculatePrecision(quote.BidPx));
        }
        if (quote.OfferPx) {
          state.form.offerPriceField = state.form.offerPriceField
            .updateValue(quote.OfferPx, true)
            .updateScale(calculatePrecision(quote.OfferPx));
        }
      }

      if (quote.QuoteStatus === QuoteStatusEnum.PendingFill || quote.QuoteStatus === QuoteStatusEnum.PendingNew) {
        state.isLoading = true;
      }
    },
    toggleLock: state => {
      state.isCustomerPriceEditable = !state.isCustomerPriceEditable;
    },
    setIsCustomerPriceEditable: (state, action: PayloadAction<boolean>) => {
      state.isCustomerPriceEditable = action.payload;
    },
    setUseDefaultAggregation: (state, action: PayloadAction<'true' | 'false'>) => {
      state.form.useDefaultAggregationField = state.form.useDefaultAggregationField.updateValue(action.payload);
      if (action.payload === 'true') {
        state.form.rfqMarketsField = state.form.rfqMarketsField.updateValue([]);
      }
    },
    setRFQMarkets: (state, action: PayloadAction<string[]>) => {
      state.form.rfqMarketsField = state.form.rfqMarketsField.updateValue(action.payload);
    },
    setQuoteRequested: state => {
      state.isLoading = true;
    },
    onQuoteRequestFailed: state => {
      state.isLoading = false;
      state.isCustomerPriceEditable = false;
    },
    resetState: state => {
      const prevSymbol = state.form.symbolField;
      const prevRFQCurrency = state.form.rfqCurrencyField;
      const newState = getInitialState();
      newState.form.symbolField = prevSymbol.setTouched(false).setDisabled(false);
      newState.form.rfqCurrencyField = prevRFQCurrency.setTouched(false);
      newState.form.rfqMarketsField = newState.form.rfqMarketsField.updateAvailableItems(
        state.dependencies.availableRFQMarketAccounts
      );
      newState.dependencies = state.dependencies;
      newState.referenceData = state.referenceData;
      return newState;
    },
    touchAll: state => {
      getTypedKeys(state.form).forEach(key => {
        if (state.form[key] instanceof BaseField) {
          // @ts-expect-error - BaseField methods have trouble lining up on keyed calls
          state.form[key] = state.form[key].setTouched(true);
        }
      });
    },
  },
});

export const {
  setReferenceData,
  setSymbol,
  setDependencies,
  setAvailableCustomerSecurities,
  setSide,
  setCustomer,
  setCustomerAccount,
  setCustomerQuantity,
  setRFQCurrency,
  setSpreadBid,
  setSpreadOffer,
  setBidSpreadFocused,
  setOfferSpreadFocused,
  setQuoteTtl,
  setRfqTtl,
  setQuoteTtlFocused,
  setRfqTtlFocused,
  setBidPriceFocused,
  setOfferPriceFocused,
  setCustomerBidPrice,
  setCustomerOfferPrice,
  onMount,
  setCurrentQuote,
  toggleLock,
  setIsCustomerPriceEditable,
  setQuoteRequested,
  onQuoteRequestFailed,
  resetState,
  setUseDefaultAggregation,
  setRFQMarkets,
  touchAll,
} = salesRFQSlice.actions;

// DERIVED state below
export const selectCanSendRfq = createSelector(
  (state: AppState) => state.salesRFQ.isLoading,
  (state: AppState) => state.salesRFQ.currentQuote?.QuoteStatus,
  (state: AppState) => state.salesRFQ.form,
  selectOfferSpreadFieldDisplayValue,
  selectBidSpreadFieldDisplayValue,
  (isLoading, quoteStatus, form, offerSpread, bidSpread) => {
    const isOpen = quoteStatus === QuoteStatusEnum.Open;
    const isSpreadFieldPopulated =
      form.sideField.value === OrderFormSides.Twoway
        ? bidSpread !== '' && offerSpread !== ''
        : form.sideField.value === OrderFormSides.Buy
          ? offerSpread !== ''
          : bidSpread !== '';
    return (
      !isOpen &&
      !isLoading &&
      !form.customerField.hasInvalidValue &&
      !form.customerAccountField.hasInvalidValue &&
      !form.symbolField.hasInvalidValue &&
      !form.customerQuantityField.hasInvalidValue &&
      isSpreadFieldPopulated
    );
  }
);

export const selectCustomerProfit = createSelector(
  (state: AppState) => state.salesRFQ.form.symbolField.value?.BaseCurrency,
  (state: AppState) => state.salesRFQ.form.rfqCurrencyField.value,
  (state: AppState) => state.salesRFQ.form.customerQuantityField.value,
  (state: AppState) => state.salesRFQ.currentQuote,
  (baseCurrency, rfqCurrency, quantity, quote) => {
    let customerBidProfit = '';
    let customerOfferProfit = '';
    if (quote) {
      if (rfqCurrency === baseCurrency) {
        customerBidProfit = Big(quote.ExpectedHedgeBidPx || '0')
          .times(quantity || '1')
          .minus(quote.BidAmt || '0')
          .toFixed();
        customerOfferProfit = Big(quote.ExpectedHedgeOfferPx || '0')
          .times(quantity || '1')
          .minus(quote.OfferAmt || '0')
          .times(-1)
          .toFixed();
      } else {
        customerBidProfit = Big(quote.ExpectedHedgeBidPx || '0')
          .minus(quote.BidPx || '0')
          .times(quote.BidAmt || '1')
          .toFixed();
        customerOfferProfit = Big(quote.ExpectedHedgeOfferPx || '0')
          .minus(quote.OfferPx || '0')
          .times(quote.OfferAmt || '1')
          .times(-1)
          .toFixed();
      }
    }
    return { customerBidProfit, customerOfferProfit };
  }
);

export const selectCustomerBalance = createSelector(
  (state: AppState) => state.salesRFQ.form.customerField.value,
  (state: AppState) => state.salesRFQ.form.customerAccountField.value,
  (state: AppState) => state.salesRFQ.form.symbolField.value,
  (state: AppState) => state.salesRFQ.dependencies.customerBalances,
  (customer?: Customer, account?: MarketAccount, security?: Security, balances?: CustomerBalance[]) => {
    return (balances || []).filter(
      b =>
        b.Counterparty === customer?.Name &&
        // For 0 balances Back-end is sending down empty MarketAccount strings
        (b.MarketAccount === account?.Name || b.MarketAccount === '') &&
        (b.Currency === security?.BaseCurrency || b.Currency === security?.QuoteCurrency)
    );
  }
);

export const salesRFQSliceReducer = salesRFQSlice.reducer;
